import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Crown } from "lucide-react"
import { UserAvatar } from "@/components/ui/user-avatar"
import { cn } from "@/lib/utils"

export const PodioPlayers = ({ players, isAdmin = false }) => {
  if (players?.length < 3) return null

  return (
    <Card className="mb-4 hidden sm:block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl font-bold">
          <Crown className="h-6 w-6 text-yellow-500" /> Podio
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "grid items-end gap-4 text-center",
            players.length === 3 && "grid-cols-3 sm:grid-cols-3",
            players.length === 4 && "grid-cols-4 sm:grid-cols-4",
            players.length >= 5 && "grid-cols-5 sm:grid-cols-5",
          )}
        >
          {/* 4th Place */}
          {players[3] && (
            <div className="flex flex-col items-center relative">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative z-10 -mb-6"
              >
                <UserAvatar
                  src={players[3].TournamentPlayer.Person.urlAvatar}
                  name={players[3].TournamentPlayer.Person.fullName}
                  className="w-14 h-14 border-2 border-background shadow-md rounded-full"
                />
              </motion.div>

              <div
                className="bg-gradient-to-b 
                from-gray-300 to-gray-500 
                text-white 
                dark:from-gray-700 dark:to-gray-900 dark:text-gray-100
                rounded-t-xl w-full p-2 shadow h-28 flex flex-col justify-end"
              >
                <div className="text-sm font-semibold">4° Lugar</div>
              </div>
              <div className="mt-1 text-sm font-bold">
                {players[3].TournamentPlayer.Person.fullName}
              </div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground">
                  {players[3].TournamentPlayer.Person.code}
                </div>
              )}
              <div className="text-sm font-semibold text-primary mt-1">{players[3].points} pts</div>
            </div>
          )}

          {/* 2nd Place */}
          {players[1] && (
            <div className="flex flex-col items-center relative">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative z-10 -mb-8"
              >
                <UserAvatar
                  src={players[1].TournamentPlayer.Person.urlAvatar}
                  name={players[1].TournamentPlayer.Person.fullName}
                  className="w-16 h-16 border-4 border-background shadow-md rounded-full"
                />
              </motion.div>
              <div
                className="bg-gradient-to-b 
                from-slate-300 to-slate-500 
                text-white 
                dark:from-slate-700 dark:to-slate-900 dark:text-gray-100
                rounded-t-2xl w-full p-4 shadow-lg h-40 flex flex-col justify-end"
              >
                <div className="text-5xl mb-1">🥈</div>
                <div className="text-lg font-semibold">2° Lugar</div>
              </div>

              <div className="mt-2 font-bold">{players[1].TournamentPlayer.Person.fullName}</div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground">
                  {players[1].TournamentPlayer.Person.code}
                </div>
              )}
              <div className="text-sm font-semibold text-primary mt-1">{players[1].points} pts</div>
            </div>
          )}

          {/* 1st Place */}
          {players[0] && (
            <div className="flex flex-col items-center relative">
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative z-10 -mb-10"
              >
                <UserAvatar
                  src={players[0].TournamentPlayer.Person.urlAvatar}
                  name={players[0].TournamentPlayer.Person.fullName}
                  className="w-20 h-20 border-4 border-background shadow-lg rounded-full"
                />
              </motion.div>
              <div
                className="bg-gradient-to-b 
                from-yellow-400 to-yellow-600 
                text-white 
                dark:from-yellow-500 dark:to-yellow-700 dark:text-gray-100
                rounded-t-2xl w-full p-6 shadow-xl h-52 flex flex-col justify-end scale-110"
              >
                <div className="text-6xl mb-1">🏆</div>
                <div className="text-lg font-bold">1° Lugar</div>
              </div>

              <div className="mt-2 font-bold text-lg">
                {players[0].TournamentPlayer.Person.fullName}
              </div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground">
                  {players[0].TournamentPlayer.Person.code}
                </div>
              )}
              <div className="text-base font-bold text-primary mt-1">{players[0].points} pts</div>
            </div>
          )}

          {/* 3rd Place */}
          {players[2] && (
            <div className="flex flex-col items-center relative">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative z-10 -mb-8"
              >
                <UserAvatar
                  src={players[2].TournamentPlayer.Person.urlAvatar}
                  name={players[2].TournamentPlayer.Person.fullName}
                  className="w-16 h-16 border-4 border-background shadow-md rounded-full"
                />
              </motion.div>
              <div
                className="bg-gradient-to-b 
                from-amber-700 to-amber-900 
                text-white 
                dark:from-amber-600 dark:to-amber-800 dark:text-gray-100
                rounded-t-2xl w-full p-4 shadow-lg h-36 flex flex-col justify-end"
              >
                <div className="text-5xl mb-1">🥉</div>
                <div className="text-lg font-semibold">3° Lugar</div>
              </div>

              <div className="mt-2 font-bold">{players[2].TournamentPlayer.Person.fullName}</div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground">
                  {players[2].TournamentPlayer.Person.code}
                </div>
              )}
              <div className="text-sm font-semibold text-primary mt-1">{players[2].points} pts</div>
            </div>
          )}

          {/* 5th Place */}
          {players[4] && (
            <div className="flex flex-col items-center relative">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative z-10 -mb-6"
              >
                <UserAvatar
                  src={players[4].TournamentPlayer.Person.urlAvatar}
                  name={players[4].TournamentPlayer.Person.fullName}
                  className="w-14 h-14 border-2 border-background shadow-md rounded-full"
                />
              </motion.div>
              <div
                className="bg-gradient-to-b 
                from-gray-400 to-gray-600 
                text-white 
                dark:from-gray-600 dark:to-gray-800 dark:text-gray-100
                rounded-t-xl w-full p-2 shadow h-24 flex flex-col justify-end"
              >
                <div className="text-sm font-semibold">5° Lugar</div>
              </div>
              <div className="mt-1 text-sm font-bold">
                {players[4].TournamentPlayer.Person.fullName}
              </div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground">
                  {players[4].TournamentPlayer.Person.code}
                </div>
              )}
              <div className="text-sm font-semibold text-primary mt-1">{players[4].points} pts</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
